/* ──────────────────────────────────────────────────────────────────────────
 * Registered User – IPO Workspace: the returning-founder command centre.
 * Built from "Returning IPO Workspace.html" and wired to REAL data from
 * /api/workspace/dashboard + /api/me/context. All CTAs open the user's own live
 * modules; "Resume" deep-links to where they left off. Scoped under `.rw`.
 * ────────────────────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import RoleSwitcher from "@/components/RoleSwitcher";
import { getContext } from "@/lib/roleContext";
import { logJourney } from "@/lib/journey";
import "./RegisteredWorkspace.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STAGES = ["Readiness", "Team", "DRHP Drafting", "SEBI Review", "Roadshow", "Listing"];

const ACTION_ROUTE = {
  Team: "/advisor-marketplace",
  Docs: "/document-vault",
  SEBI: "/drhp-workspace?phase=DATA_COLLECTION",
  Finance: "/ipo-navigator",
  Issue: "/ipo-navigator",
  Setup: "/drhp-workspace?phase=DATA_COLLECTION",
};
const ACTION_ICON = {
  Team: { i: "ti-users", bg: "#e1f5ee", c: "#085041" },
  Docs: { i: "ti-upload", bg: "#e6f1fb", c: "#0c447c" },
  SEBI: { i: "ti-file-text", bg: "#eef0fb", c: "#3C3489" },
  Finance: { i: "ti-cash", bg: "#fdf3e3", c: "#a86711" },
  Issue: { i: "ti-chart-bar", bg: "#fdecec", c: "#c0392b" },
  Setup: { i: "ti-alert-triangle", bg: "#fdecec", c: "#c0392b" },
};

const firstNameOf = (n) => (n || "").trim().split(/\s+/)[0] || "there";
const dateAgo = (iso) => {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 36e5;
    if (diff < 1) return `${Math.max(1, Math.round(diff * 60))} min ago`;
    if (diff < 24) return `${Math.round(diff)} hr ago`;
    return `${Math.round(diff / 24)}d ago`;
  } catch { return ""; }
};

export const RegisteredWorkspace = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [est, setEst] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/workspace/dashboard`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then(setD).catch(() => {});
    fetch(`${API_URL}/api/cost-estimator`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then(setEst).catch(() => {});
    getContext().then(setCtx);
    logJourney("IPO Navigator", "Viewed", "Opened Registered IPO Workspace");
  }, []);

  if (!d) return <div className="rw"><div className="rw-loading" data-testid="rw-loading">Loading your workspace…</div></div>;

  const k = d.kpis || {};
  const cost = d.cost || {};
  const stage = d.stage || 1;
  const readiness = k.readiness || 0;
  const dash = 226;
  const offset = dash - (dash * readiness) / 100;
  const firstName = firstNameOf(ctx?.name);
  const actions = d.actions || [];
  const resumePath = ctx?.resume?.path || "/ipo-navigator";

  return (
    <div className="rw" data-testid="registered-workspace-page">
      {/* HEADER */}
      <div className="hd">
        <div className="brand" onClick={() => navigate("/registered-workspace")} style={{ cursor: "pointer" }} data-testid="rw-brand">IPO<span>Labs</span></div>
        <div className="gs"><i className="ti ti-search" aria-hidden="true" /><input placeholder="Search advisors, documents, chapters…" onKeyDown={(e) => e.key === "Enter" && navigate("/ipo-navigator")} /></div>
        <div className="hd-r">
          <span className="hic" onClick={() => navigate("/ipo-navigator")}><i className="ti ti-bell" aria-hidden="true" />{actions.length > 0 && <span className="hbadge">{actions.length}</span>}</span>
          <span className="hic" onClick={() => navigate("/oracle")}><i className="ti ti-sparkles" aria-hidden="true" /></span>
          <RoleSwitcher accent="#185FA5" currentRole="workspace" label={d.companyName || "My workspace"} />
        </div>
      </div>

      {/* WELCOME HERO */}
      <div className="hero">
        <div className="hero-l">
          <div className="hero-kick">Welcome back · Stage {stage} of 6</div>
          <div className="hero-h">Good to see you, {firstName}.</div>
          <div className="hero-d">
            Your DRHP is {k.drhpProgress || 0}% drafted{d.companyName ? ` for ${d.companyName}` : ""}.
            {actions.length > 0 ? ` ${actions.length} thing${actions.length === 1 ? "" : "s"} need${actions.length === 1 ? "s" : ""} you today — start with the most urgent below.` : " You're all caught up — great work."}
          </div>
          <div className="hero-cta">
            <button className="hbtn-w" onClick={() => navigate(resumePath)} data-testid="rw-resume">Resume my IPO →</button>
            <button className="hbtn-o" onClick={() => navigate("/ipo-navigator")} data-testid="rw-needs-today">What needs me today</button>
          </div>
        </div>
        <div className="ring-w">
          <svg className="ring" viewBox="0 0 88 88">
            <circle cx="44" cy="44" r="36" fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="7" />
            <circle cx="44" cy="44" r="36" fill="none" stroke="#5fd39a" strokeWidth="7" strokeDasharray={dash} strokeDashoffset={offset} strokeLinecap="round" transform="rotate(-90 44 44)" />
            <text x="44" y="49" textAnchor="middle" fontSize="18" fontWeight="600" fill="#fff" fontFamily="DM Sans">{readiness}%</text>
          </svg>
          <div className="ring-c">Overall<b>readiness</b></div>
        </div>
      </div>

      {/* STAGE STRIP */}
      <div className="stagestrip">
        {STAGES.map((s, i) => {
          const n = i + 1;
          const cls = n < stage ? "done" : n === stage ? "now" : "next";
          return (
            <div className={`ss ${cls}`} key={s}>
              <span className="ss-line" />
              <span className={`ss-dot ss-${cls === "next" ? "next" : cls === "now" ? "now" : "done"}`}>{n < stage ? <i className="ti ti-check" aria-hidden="true" /> : n}</span>
              <span className="ss-l">{s}</span>
            </div>
          );
        })}
      </div>

      {/* KPI CARDS */}
      <div className="kpis">
        <div className="kk" onClick={() => navigate("/drhp-workspace?phase=DATA_COLLECTION")} style={{ cursor: "pointer" }} data-testid="rw-kpi-drhp">
          <div className="kk-ic" style={{ color: "#185FA5" }}><i className="ti ti-file-text" aria-hidden="true" /></div>
          <div className="kk-v">{k.drhpProgress || 0}%</div><div className="kk-l">DRHP drafted</div>
          <div className="kk-sub" style={{ color: k.drhpSynced ? "#16a34a" : "#d97706" }}>{k.drhpSynced ? "master data synced" : "sync pending"}</div>
        </div>
        <div className="kk" onClick={() => navigate("/advisor-marketplace")} style={{ cursor: "pointer" }} data-testid="rw-kpi-advisors">
          <div className="kk-ic" style={{ color: "#0F6E56" }}><i className="ti ti-users" aria-hidden="true" /></div>
          <div className="kk-v">{k.advisorsEngaged || 0} / {k.advisorsTotal || 0}</div><div className="kk-l">Core advisors engaged</div>
          <div className="kk-sub" style={{ color: (k.advisorsTotal - k.advisorsEngaged) > 0 ? "#d97706" : "#16a34a" }}>{(k.advisorsTotal - k.advisorsEngaged) > 0 ? `${k.advisorsTotal - k.advisorsEngaged} role(s) open` : "team complete"}</div>
        </div>
        <div className="kk" onClick={() => navigate("/ipo-navigator")} style={{ cursor: "pointer" }} data-testid="rw-kpi-cost">
          <div className="kk-ic" style={{ color: "#a86711" }}><i className="ti ti-wallet" aria-hidden="true" /></div>
          <div className="kk-v">₹{cost.spent || 0}L</div><div className="kk-l">Spent{cost.totalBudget ? ` of ₹${cost.totalBudget}L` : ""}</div>
          <div className="kk-sub" style={{ color: "#16a34a" }}>{cost.pctOfIssue ? `${cost.pctOfIssue}% of issue` : "track your costs"}</div>
        </div>
        <div className="kk" onClick={() => navigate("/document-vault")} style={{ cursor: "pointer" }} data-testid="rw-kpi-docs">
          <div className="kk-ic" style={{ color: "#dc2626" }}><i className="ti ti-files" aria-hidden="true" /></div>
          <div className="kk-v">{k.documentsPct || 0}%</div><div className="kk-l">Documents ready</div>
          <div className="kk-sub" style={{ color: (k.documentsMandatoryMissing || 0) > 0 ? "#dc2626" : "#16a34a" }}>{(k.documentsMandatoryMissing || 0) > 0 ? `${k.documentsMandatoryMissing} mandatory missing` : "all mandatory in"}</div>
        </div>
      </div>

      {/* BODY */}
      <div className="body">
        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-t"><i className="ti ti-alert-circle" style={{ color: "#dc2626" }} aria-hidden="true" /> Needs you today <span className="more" onClick={() => navigate("/ipo-navigator")} data-testid="rw-view-all">View all →</span></div>
            {actions.length === 0 ? (
              <div className="rw-empty">Nothing urgent right now — your workspace is in good shape. 🎉</div>
            ) : actions.map((a, i) => {
              const ic = ACTION_ICON[a.tag] || { i: "ti-point", bg: "#eef2f7", c: "#185FA5" };
              const route = ACTION_ROUTE[a.tag] || "/ipo-navigator";
              return (
                <div className="att" key={i} onClick={() => navigate(route)} data-testid={`rw-action-${i}`}>
                  <div className="att-ic" style={{ background: ic.bg, color: ic.c }}><i className={`ti ${ic.i}`} aria-hidden="true" /></div>
                  <div className="att-mid"><div className="att-h">{a.text}</div><div className="att-d">{a.tag} · needs your attention</div></div>
                  <span className="att-go">Go →</span>
                </div>
              );
            })}
          </div>

          <div className="card">
            <div className="card-t"><i className="ti ti-grid-dots" style={{ color: "#185FA5" }} aria-hidden="true" /> Jump back into</div>
            <div className="qmods">
              <div className="qm" onClick={() => navigate("/drhp-workspace?phase=DATA_COLLECTION")} data-testid="rw-jump-drhp"><div className="qm-ic" style={{ background: "#e6f1fb", color: "#0C447C" }}><i className="ti ti-file-text" aria-hidden="true" /></div><div className="qm-h">DRHP Workspace</div><div className="qm-d">{k.drhpProgress || 0}% drafted</div><span className="qm-badge" style={{ color: k.drhpSynced ? "#16a34a" : "#d97706" }}>{k.drhpSynced ? "synced" : "sync pending"}</span></div>
              <div className="qm" onClick={() => navigate("/advisor-marketplace")} data-testid="rw-jump-advisors"><div className="qm-ic" style={{ background: "#e1f5ee", color: "#085041" }}><i className="ti ti-users" aria-hidden="true" /></div><div className="qm-h">Advisor Marketplace</div><div className="qm-d">{k.advisorsEngaged || 0} of {k.advisorsTotal || 0} engaged</div><span className="qm-badge" style={{ color: (k.advisorsTotal - k.advisorsEngaged) > 0 ? "#d97706" : "#16a34a" }}>{(k.advisorsTotal - k.advisorsEngaged) > 0 ? `${k.advisorsTotal - k.advisorsEngaged} open` : "complete"}</span></div>
              <div className="qm" onClick={() => navigate("/ipo-navigator")} data-testid="rw-jump-cost"><div className="qm-ic" style={{ background: "#fdf3e3", color: "#a86711" }}><i className="ti ti-wallet" aria-hidden="true" /></div><div className="qm-h">Cost Tracker</div><div className="qm-d">₹{cost.due || 0}L due</div><span className="qm-badge" style={{ color: "#16a34a" }}>{cost.lineItems || 0} items</span></div>
              <div className="qm" onClick={() => navigate("/document-vault")} data-testid="rw-jump-docs"><div className="qm-ic" style={{ background: "#eef0fb", color: "#3C3489" }}><i className="ti ti-files" aria-hidden="true" /></div><div className="qm-h">Document Vault</div><div className="qm-d">{k.documentsPct || 0}% ready</div><span className="qm-badge" style={{ color: (k.documentsMandatoryMissing || 0) > 0 ? "#dc2626" : "#16a34a" }}>{(k.documentsMandatoryMissing || 0) > 0 ? `${k.documentsMandatoryMissing} missing` : "ready"}</span></div>
              <div className="qm" onClick={() => navigate("/ipo-cost-estimator")} data-testid="rw-jump-estimate"><div className="qm-ic" style={{ background: "#eef4fb", color: "#185FA5" }}><i className="ti ti-calculator" aria-hidden="true" /></div><div className="qm-h">IPO Cost Estimate</div><div className="qm-d">{est?.current ? est.current.range_label : "Not estimated yet"}</div><span className="qm-badge" style={{ color: est?.current ? "#16a34a" : "#d97706" }}>{est?.current ? `v${est.current.version} · ${est.current.listing_type.toUpperCase()}` : "estimate now"}</span></div>
            </div>
          </div>
        </div>

        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-t"><i className="ti ti-activity" style={{ color: "#185FA5" }} aria-hidden="true" /> Team activity</div>
            {(d.activity || []).length === 0 ? (
              <div className="rw-empty">No recent activity yet — it'll show here as your team works.</div>
            ) : (d.activity || []).map((a, i) => (
              <div className="act" key={i} data-testid={`rw-activity-${i}`}>
                <span className="act-dot" style={{ background: ["#16a34a", "#6C4BF4", "#d97706"][i % 3] }} />
                <div><div className="act-t">{a.by ? <b>{a.by} </b> : null}{a.text}</div><div className="act-time">{dateAgo(a.date)}</div></div>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-t"><i className="ti ti-sparkles" style={{ color: "#185FA5" }} aria-hidden="true" /> Ask Oracle {"{IPO.GPT}"}</div>
            <div className="att" style={{ border: "none", paddingTop: 0 }} onClick={() => navigate("/oracle")} data-testid="rw-oracle-1">
              <div className="att-ic" style={{ background: "#e6f1fb", color: "#0c447c" }}><i className="ti ti-message-2" aria-hidden="true" /></div>
              <div className="att-mid"><div className="att-h">"What happens after I file the DRHP?"</div><div className="att-d">Get a plain-language answer for your next stage</div></div>
            </div>
            <div className="att" style={{ border: "none" }} onClick={() => navigate("/oracle")} data-testid="rw-oracle-2">
              <div className="att-ic" style={{ background: "#e6f1fb", color: "#0c447c" }}><i className="ti ti-message-2" aria-hidden="true" /></div>
              <div className="att-mid"><div className="att-h">"Explain anchor lock-in rules"</div><div className="att-d">Prep for your Stage 5 roadshow</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisteredWorkspace;
